import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Grid,
    Card,
    CardContent,
    Button,
    Chip,
    Stack,
    CircularProgress,
    Container,
    Avatar,
    Divider,
    Snackbar,
    Alert
} from '@mui/material';
import axios from 'axios';
import { useSelector } from 'react-redux';
import WorkIcon from '@mui/icons-material/Work';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AssignmentIcon from '@mui/icons-material/Assignment';
import AccessTimeIcon from '@mui/icons-material/AccessTime';

const JobsBoard = () => {
    const token = useSelector((state) => state.auth.token);
    const [tasks, setTasks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [claimLoading, setClaimLoading] = useState(null); // ID of task being claimed
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

    useEffect(() => {
        fetchAvailableProjects();
    }, [token]);

    const fetchAvailableProjects = async () => {
        setLoading(true);
        try {
            const res = await axios.get('/api/projects/available', {
                headers: { Authorization: `Bearer ${token}` }
            });
            setTasks(res.data); // keeping variable name 'tasks' for minimal diff, but it holds projects
        } catch (err) {
            console.error('Error fetching jobs:', err);
            setSnackbar({ open: true, message: 'Failed to load available jobs', severity: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleClaim = async (projectId) => {
        setClaimLoading(projectId);
        try {
            await axios.put(`/api/projects/${projectId}/claim`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });

            // Optional: Don't remove it, just show success? Or remove it? 
            // Usually if I claim a project I might still want to see it, but for a "Job Board" usually it disappears or marks as claimed.
            // Let's remove it for now to simulate "taking" the job.
            setTasks(prev => prev.filter(p => p._id !== projectId));
            setSnackbar({ open: true, message: 'Project claimed successfully!', severity: 'success' });
        } catch (err) {
            console.error('Error claiming project:', err);
            setSnackbar({ open: true, message: 'Failed to claim project', severity: 'error' });
        } finally {
            setClaimLoading(null);
        }
    };

    // Initial fetch
    useEffect(() => {
        fetchAvailableProjects();
    }, [token]);

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Container maxWidth="xl" sx={{ py: 4 }}>
            <Box mb={4}>
                <Typography variant="h4" fontWeight={700} gutterBottom>
                    Project Job Board
                </Typography>
                <Typography variant="body1" color="text.secondary">
                    Browse available translation projects and claim them to start working.
                </Typography>
            </Box>

            {tasks.length === 0 ? (
                <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                    minHeight="40vh"
                    textAlign="center"
                    bgcolor="background.paper"
                    borderRadius={4}
                    p={4}
                >
                    <WorkIcon sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary">
                        No available projects right now.
                    </Typography>
                </Box>
            ) : (
                <Grid container spacing={3}>
                    {tasks.map(project => (
                        <Grid item xs={12} sm={6} md={4} key={project._id}>
                            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', borderRadius: 3, transition: 'box-shadow 0.3s', '&:hover': { boxShadow: 4 } }}>
                                <CardContent sx={{ flexGrow: 1 }}>
                                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={2}>
                                        <Chip
                                            label={project.sourceLanguage?.toUpperCase() + ' → ' + project.targetLanguage?.toUpperCase()}
                                            size="small"
                                            color="primary"
                                            variant="outlined"
                                        />
                                        <Chip
                                            label={`${project.wordCount || 0} words`}
                                            size="small"
                                        />
                                    </Stack>

                                    <Typography variant="h6" fontWeight={600} gutterBottom>
                                        {project.projectName}
                                    </Typography>

                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                        Posted by: {project.createdBy?.firstName} {project.createdBy?.lastName}
                                    </Typography>

                                    <Divider sx={{ my: 2 }} />

                                    <Stack spacing={1}>
                                        <Box display="flex" alignItems="center" color="text.secondary">
                                            <CalendarTodayIcon fontSize="small" sx={{ mr: 1 }} />
                                            <Typography variant="caption">
                                                Created: {new Date(project.createdAt).toLocaleDateString()}
                                            </Typography>
                                        </Box>
                                        {project.deadline && (
                                            <Box display="flex" alignItems="center" color="error.main">
                                                <AccessTimeIcon fontSize="small" sx={{ mr: 1 }} />
                                                <Typography variant="caption" fontWeight={600}>
                                                    Deadline: {new Date(project.deadline).toLocaleDateString()}
                                                </Typography>
                                            </Box>
                                        )}
                                    </Stack>
                                </CardContent>
                                <Box p={2} pt={0}>
                                    <Button
                                        variant="contained"
                                        fullWidth
                                        onClick={() => handleClaim(project._id)}
                                        disabled={claimLoading === project._id}
                                    >
                                        {claimLoading === project._id ? 'Joining...' : 'Claim Project'}
                                    </Button>
                                </Box>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            )}

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default JobsBoard;
